export type AuthTokenPayload = {
  sub: string;
  /** Papel principal: decide a home e o escopo de lojas. */
  userType: "ADMIN" | "SUPERVISOR" | "SUPERVISOR_DE_CAMPO" | "DP" | "FINANCEIRO" | "FINANCEIRO_LEITURA" | "GESTOR_CAMPANHAS" | string;
  /**
   * Papéis efetivos: principal + adicionais (ADR 0024, 04/10/2026). Ausente em
   * token emitido antes disso — ler sempre por `getRolesFromToken`.
   */
  roles?: string[];
  /**
   * Módulos retirados do administrador restrito (ADR 0027, 11/10/2026). Ausente
   * nos demais e em token anterior — ler sempre por `getRevokedModulesFromToken`.
   */
  revokedModules?: string[];
  storeIds: string[];
  email: string;
  iat: number;
  exp: number;
};

function base64UrlToBase64(input: string): string {
  return input.replace(/-/g, "+").replace(/_/g, "/");
}

export function decodeJwtPayload<T = any>(token: string): T | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;

    const base64 = base64UrlToBase64(parts[1]);
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");

    const json = atob(padded);
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

export function getAuthPayload(token: string | null): AuthTokenPayload | null {
  if (!token) return null;
  const normalized = token.includes(" ") ? token.split(" ").pop()! : token;
  return decodeJwtPayload<AuthTokenPayload>(normalized);
}


export function isTokenExpired(payload: { exp: number } | null): boolean {
  if (!payload?.exp) return true;
  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSec;
}

export function isLoggedInFromToken(token: string | null): boolean {
  const payload = getAuthPayload(token);
  if (!payload) return false;
  return !isTokenExpired(payload);
}

const normalizeRole = (role: unknown) => String(role ?? "").trim().toUpperCase();

/**
 * Papéis efetivos do token (principal + adicionais, ADR 0024). Token sem `roles`
 * (emitido antes de 04/10/2026) cai para o `userType`. Expirado ou inválido: [].
 */
export function getRolesFromToken(token: string | null): string[] {
  const payload = getAuthPayload(token);
  if (!payload || isTokenExpired(payload)) return [];

  if (Array.isArray(payload.roles) && payload.roles.length) {
    return payload.roles.map(normalizeRole);
  }
  const userType = normalizeRole(payload.userType);
  return userType ? [userType] : [];
}

/** Papel principal (o `userType`), normalizado. Expirado ou inválido: "". */
export function getPrincipalRoleFromToken(token: string | null): string {
  const payload = getAuthPayload(token);
  if (!payload || isTokenExpired(payload)) return "";
  return normalizeRole(payload.userType);
}

/** Verdadeiro se ALGUM papel efetivo está na lista. */
function isUserTypeIn(token: string | null, types: string[]): boolean {
  const roles = getRolesFromToken(token);
  return types.map(normalizeRole).some((t) => roles.includes(t));
}

/**
 * ADMIN ou administrador restrito: o restrito leva `ADMIN` nos papéis efetivos
 * (ADR 0027). O módulo retirado dele se testa com `hasModuleFromToken`.
 */
export function isAdminFromToken(token: string | null): boolean {
  return isUserTypeIn(token, ["ADMIN"]);
}

/** ADMIN pleno (papel principal): o único que mexe em tipo, acessos e direitos de usuário. */
export function isFullAdminFromToken(token: string | null): boolean {
  return getPrincipalRoleFromToken(token) === "ADMIN";
}

/** Módulos retirados do administrador restrito; vazio para os demais. */
export function getRevokedModulesFromToken(token: string | null): string[] {
  const payload = getAuthPayload(token);
  if (!payload || isTokenExpired(payload) || !Array.isArray(payload.revokedModules)) return [];
  return payload.revokedModules.map(normalizeRole);
}

/** Falso só quando o módulo foi retirado (administrador restrito). */
export function hasModuleFromToken(token: string | null, module: string): boolean {
  return !getRevokedModulesFromToken(token).includes(normalizeRole(module));
}

export function isSupervisorFromToken(token: string | null): boolean {
  return isUserTypeIn(token, ["SUPERVISOR", "SUPERVISOR_DE_CAMPO"]);
}

/** Papel DP (Departamento Pessoal), acrescentado em 21/09/2026. */
export function isDpFromToken(token: string | null): boolean {
  return isUserTypeIn(token, ["DP"]);
}

/**
 * Quem enxerga o modulo de DP. Decidido em 21/09/2026: ADMIN ve tudo do DP e
 * supervisor nao ve nada — por isso a lista tem exatamente estes dois papeis.
 */
export function isDpOrAdminFromToken(token: string | null): boolean {
  return isUserTypeIn(token, ["ADMIN", "DP"]);
}

/**
 * Quem enxerga o módulo Financeiro (04/10/2026): ADMIN, FINANCEIRO e
 * FINANCEIRO_LEITURA — como papel principal ou adicional.
 */
export function isFinanceiroOrAdminFromToken(token: string | null): boolean {
  return isUserTypeIn(token, ["ADMIN", "FINANCEIRO", "FINANCEIRO_LEITURA"]);
}

/** Quem ESCREVE no Financeiro: ADMIN e FINANCEIRO. FINANCEIRO_LEITURA só consulta. */
export function canWriteFinanceiroFromToken(token: string | null): boolean {
  return isUserTypeIn(token, ["ADMIN", "FINANCEIRO"]);
}

/**
 * Quem usa o controle de vasilhame RGB (24/09/2026): ADMIN e supervisores.
 * DP fica de fora — vasilhame não é assunto de Departamento Pessoal.
 */
/** Quem usa o criador de campanhas (31/10/2026): ADMIN e o gestor de campanhas, principal ou adicional. */
export function isCampanhasUserFromToken(token: string | null): boolean {
  return isUserTypeIn(token, ["ADMIN", "GESTOR_CAMPANHAS"]);
}

export function isRgbUserFromToken(token: string | null): boolean {
  return isUserTypeIn(token, ["ADMIN", "SUPERVISOR", "SUPERVISOR_DE_CAMPO"]);
}
